import type { RendererElement, RendererNode, VNode } from 'vue';
import type { TSelectProps } from './TSelect.types';

export interface SelectOption {
  value: TSelectProps['value'];
  label: string;
  disabled: boolean;
}

type Slot = VNode<
  RendererNode,
  RendererElement,
  {
    [key: string]: any;
  }
>;

const getTextFromSlotChildren = (children: unknown): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children
      .map((child) => getTextFromSlotChildren(child))
      .join('')
      .trim();
  }

  if (children && typeof children === 'object' && 'children' in children) {
    return getTextFromSlotChildren((children as { children?: unknown }).children);
  }

  return '';
};

const getOptionsFromSlots = (slots: Slot[], results: Slot[] = []) => {
  slots?.forEach((slot) => {
    if (slot.type === 'option') {
      results.push(slot);
    } else if (Array.isArray(slot.children)) {
      getOptionsFromSlots(slot.children as Slot[], results);
    }
  });

  return results;
};

export const buildSelectOptions = (slotVNodes: Slot[]): SelectOption[] => {
  const slots = getOptionsFromSlots(slotVNodes);
  return slots.map((option) => ({
    value: option.props?.value,
    disabled: Boolean(option.props?.disabled),
    label: getTextFromSlotChildren(option.children),
  }));
};

export const isOptionValueEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(a);
  if (proto !== Object.prototype || Object.getPrototypeOf(b) !== proto) {
    return false;
  }
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every((k) => (a as Record<string, unknown>)[k] === (b as Record<string, unknown>)[k]);
};

export const getInitialHighlightedIndex = (
  options: SelectOption[],
  currentValue: unknown,
): number => {
  const selectedIndex = options.findIndex((opt) => isOptionValueEqual(opt.value, currentValue) && !opt.disabled);
  if (selectedIndex >= 0) {
    return selectedIndex;
  }
  const firstEnabled = options.findIndex((opt) => !opt.disabled);
  return firstEnabled >= 0 ? firstEnabled : -1;
};

export const resolveLeftIcon = (props: Record<string, unknown>): string => {
  if (typeof props.leftIcon === 'string') {
    return props.leftIcon;
  }
  const attr = props['left-icon'];
  return typeof attr === 'string' ? attr : '';
};

export const getLeftIconName = (icon: string, iconSet: string): string => {
  if (!icon) {
    return '';
  }
  return icon.includes(':') ? icon : `${iconSet}:${icon}`;
};
